import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { getAdminConsultorioGrid } from '../lib/api';
import { formatRoomUnit, roomLabelOf } from '../lib/roomLabel';
import AppointmentGridDetailCard from './AppointmentGridDetailCard';
import { formatYmdClinic, todayYmdInClinic } from '../lib/clinicTime';
import { useMexicoCityClock } from '../hooks/useMexicoCityClock';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const GROUP_STYLES = {
  sky: {
    header: 'bg-sky-100 border-sky-300 text-sky-950',
    occupied: 'bg-sky-200/90 border-sky-300 text-sky-950',
    legend: 'bg-sky-200 border-sky-400',
  },
  amber: {
    header: 'bg-amber-100 border-amber-300 text-amber-950',
    occupied: 'bg-amber-200/90 border-amber-300 text-amber-950',
    legend: 'bg-amber-200 border-amber-400',
  },
  slate: {
    header: 'bg-slate-100 border-slate-300 text-slate-900',
    occupied: 'bg-slate-200/90 border-slate-300 text-slate-900',
    legend: 'bg-slate-200 border-slate-400',
  },
};

function groupStyle(color) {
  return GROUP_STYLES[color] || GROUP_STYLES.slate;
}

function columnHeaderLabel(col) {
  return formatRoomUnit(roomLabelOf(col.room_label), col.consultorio);
}

export default function ConsultorioOccupancyGrid({
  dateYmd,
  refreshToken = 0,
  onConfirmAppointment,
  onCancelAppointment,
  onCompleteAppointment,
  onOpenClinicalHistory,
  onNoShowAppointment,
}) {
  const filterDay = String(dateYmd || '').slice(0, 10);
  const { now } = useMexicoCityClock();
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadGrid = useCallback(async () => {
    setLoading(true);
    try {
      if (!filterDay) {
        setGrid(null);
        return;
      }
      const data = await getAdminConsultorioGrid(filterDay);
      setGrid(data);
    } catch {
      toast.error('No se pudo cargar la ocupación por lugar');
      setGrid(null);
    } finally {
      setLoading(false);
    }
  }, [filterDay]);

  useEffect(() => {
    loadGrid();
  }, [loadGrid, refreshToken]);

  const legendGroups = useMemo(() => {
    const cols = grid?.columns || [];
    const seen = new Map();
    for (const col of cols) {
      if (!seen.has(col.type_id)) {
        seen.set(col.type_id, {
          type_id: col.type_id,
          type_label: col.type_label,
          room_label: roomLabelOf(col.room_label),
          group_color: col.group_color,
          count: 0,
        });
      }
      seen.get(col.type_id).count += 1;
    }
    return [...seen.values()];
  }, [grid]);

  const dayLabel = filterDay ? formatYmdClinic(filterDay, "EEEE d 'de' MMMM yyyy") : '';
  const isToday = filterDay === todayYmdInClinic();

  return (
    <Card className="border border-border" data-testid="consultorio-occupancy-grid">
      <CardHeader className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle className="font-heading text-xl">Ocupación por lugar</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Día seleccionado · {dayLabel}
              {isToday ? (
                <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  Hoy
                </span>
              ) : null}
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={loadGrid} disabled={loading || !filterDay}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
        {legendGroups.length > 0 ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {legendGroups.map((g) => {
              const st = groupStyle(g.group_color);
              return (
                <span key={g.type_id} className="inline-flex items-center gap-2">
                  <span className={`inline-block w-4 h-4 rounded border ${st.legend}`} aria-hidden />
                  <span>
                    {g.type_label} · {g.count}{' '}
                    {g.count === 1
                      ? (g.room_label || 'Consultorio').toLowerCase()
                      : `${(g.room_label || 'Consultorio').toLowerCase()}s`}
                  </span>
                </span>
              );
            })}
            <span className="text-muted-foreground">· Libre = disponible · Cerrado = fuera de horario o bloqueado</span>
          </div>
        ) : null}
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center animate-pulse">Cargando grilla…</p>
        ) : !grid?.available ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            {grid?.reason || 'Sin datos para este día'}
          </p>
        ) : !grid.time_slots?.length ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No hay franjas horarias este día</p>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[min(70vh,42rem)] rounded-lg border border-border">
            <table className="w-full min-w-[960px] border-collapse text-sm">
              <thead className="sticky top-0 z-20">
                <tr>
                  <th className="sticky left-0 z-30 bg-muted border-b border-r border-border px-3 py-2 text-left font-semibold min-w-[5rem]">
                    Hora
                  </th>
                  {(grid.columns || []).map((col) => {
                    const st = groupStyle(col.group_color);
                    return (
                      <th
                        key={col.key}
                        className={`border-b border-r border-border px-2 py-2 text-center font-semibold text-xs sm:text-sm ${st.header}`}
                      >
                        {columnHeaderLabel(col)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {grid.time_slots.map((slot) => (
                  <tr key={slot} className="border-b border-border last:border-b-0">
                    <td className="sticky left-0 z-10 bg-background border-r border-border px-3 py-2 font-mono font-medium whitespace-nowrap">
                      {slot}
                    </td>
                    {(grid.columns || []).map((col) => {
                      const row = grid.occupancy?.[slot] || {};
                      const inSchedule = Object.prototype.hasOwnProperty.call(row, col.key);
                      const cell = row[col.key];
                      if (!inSchedule) {
                        return (
                          <td
                            key={`${slot}-${col.key}`}
                            className="border-r border-border px-2 py-2 text-center bg-muted/20 text-muted-foreground/40"
                          >
                            —
                          </td>
                        );
                      }
                      if (cell?.blocked) {
                        return (
                          <td
                            key={`${slot}-${col.key}`}
                            className="border-r border-border px-2 py-2 text-center bg-muted/25"
                          >
                            <span className="text-xs text-muted-foreground/50">Cerrado</span>
                          </td>
                        );
                      }
                      if (!cell) {
                        return (
                          <td
                            key={`${slot}-${col.key}`}
                            className="border-r border-border px-2 py-2 text-center bg-muted/10"
                          >
                            <span className="text-xs text-muted-foreground/60">Libre</span>
                          </td>
                        );
                      }
                      return (
                        <td
                          key={`${slot}-${col.key}`}
                          className="border-r border-border px-1.5 py-1.5 align-top min-w-[11.5rem] max-w-[14rem]"
                        >
                          <AppointmentGridDetailCard
                            cell={cell}
                            gridDateYmd={filterDay}
                            now={now}
                            onConfirm={onConfirmAppointment}
                            onCancel={onCancelAppointment}
                            onComplete={onCompleteAppointment}
                            onClinicalHistory={onOpenClinicalHistory}
                            onNoShow={onNoShowAppointment}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
